import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import { SuccessSlideType } from "../../../types/successSlide";
import { OnNavigationType } from "../_basic/SlideNavigation";

interface SuccessSlideProps {
  data: SuccessSlideType;
  onNavigation?: OnNavigationType;
  setConsentCheckPoints?: (checkpoints: {
    enpalMarketingConsent: boolean;
    thirdPartyMarketing: boolean;
    thirdPartyProfiling: boolean;
  }) => void;
  handleUpdate: any;
  dataUpdated: any;
  sendGtagEvent: any;
  sendAnalyticsData: any;
}

const SuccessSlide: React.FC<SuccessSlideProps> = ({
  data,
  onNavigation,
  setConsentCheckPoints,
  handleUpdate,
  dataUpdated,
  sendGtagEvent,
  sendAnalyticsData,
}) => {
  const [isMarketingConsentChecked, setIsMarketingConsentChecked] =
    useState(false);
  const [isThirdPartyMarketingChecked, setIsThirdPartyMarketingChecked] =
    useState(false);
  const [isThirdPartyProfilingChecked, setIsThirdPartyProfilingChecked] =
    useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => setIsExpanded((prev) => !prev);

  const handleRedirect = () => {
    if (data.redirectLink) {
      window.location.href = data.redirectLink;
    }
    onNavigation && onNavigation(1);
  };

  const redirectPhone = () => {
    sendGtagEvent("gaEvent", "ThankYouPage", "CallMe_Button", "click");
    if (data.contact_number?.value) {
      window.location.href = `tel:${data.contact_number.value}`;
    }
  };

  function getIconUrl(data: any) {
    const prefix = "https://slider-assets.cust.enpal.io/slider-images/";

    if (data.icon && data.icon.startsWith("https://")) {
      return data.icon;
    } else {
      return `${prefix}${data.icon}`;
    }
  }

  useEffect(() => {
    if (isMarketingConsentChecked === true) {
      setIsThirdPartyMarketingChecked(true);
      setIsThirdPartyProfilingChecked(true);
    }
  }, [isMarketingConsentChecked]);

  useEffect(() => {
    if (!isThirdPartyMarketingChecked || !isThirdPartyProfilingChecked) {
      setIsMarketingConsentChecked(false);
    }
    if (isThirdPartyMarketingChecked && isThirdPartyProfilingChecked) {
      setIsMarketingConsentChecked(true);
    }
  }, [isThirdPartyMarketingChecked, isThirdPartyProfilingChecked]);

  useEffect(() => {
    setConsentCheckPoints?.({
      enpalMarketingConsent: isMarketingConsentChecked,
      thirdPartyMarketing: isThirdPartyMarketingChecked,
      thirdPartyProfiling: isThirdPartyProfilingChecked,
    });
  }, [
    isMarketingConsentChecked,
    isThirdPartyMarketingChecked,
    isThirdPartyProfilingChecked,
  ]);

  const handleFormSubmit = () => {
    if (!isFormValid()) return;
    if (handleUpdate) {
      if (data?.marketingAndProfiling) {
        sendAnalyticsData("marketing", isMarketingConsentChecked);
        sendAnalyticsData("thirdPartyMarketing", isThirdPartyMarketingChecked);
        sendAnalyticsData("thirdPartyProfiling", isThirdPartyProfilingChecked);
      }
      sendAnalyticsData("contact_form_ThankYouPage", "continue_button");
      handleUpdate();
    }
  };

  const handleMarketingConsentChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setIsMarketingConsentChecked(checked);
  };

  const handleThirdPartyMarketingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setIsThirdPartyMarketingChecked(checked);
  };

  const handleThirdPartyProfilingChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setIsThirdPartyProfilingChecked(checked);
  };
  const isFormValid = () => {
    const conditions = [
      {
        mandatory: data?.marketingAndProfiling?.mandatory,
        check: isMarketingConsentChecked,
      },
      {
        mandatory: data?.thirdPartyMarketing?.mandatory,
        check: isThirdPartyMarketingChecked,
      },
      {
        mandatory: data?.thirdPartyProfiling?.mandatory,
        check: isThirdPartyProfilingChecked,
      },
    ];

    return conditions.some((condition) => condition.check);
  };

  return (
    <>
      <div className={styles.sucessSlide}>
        <div className={styles.iconContainer}>
          <img
            src={getIconUrl(data)}
            alt="Success Icon"
            className={styles.icon}
          />
        </div>
        <h1 className={styles.title}>{data.title}</h1>
        <p className={styles.description}>{data.description}</p>
        {!dataUpdated && (
          <>
            <div
              className={`${styles.marketingContainer} ${
                isExpanded ? styles.expanded : ""
              }`}>
              {data.marketingAndProfiling && (
                <>
                  <div className={styles.checkBoxTitle}>
                    {data.marketingAndProfiling.title}
                  </div>
                  <div className={styles.marketingCheckBox}>
                    <label
                      className={`${styles.customCheckbox} ${styles.checkBox}`}>
                      <input
                        type="checkbox"
                        onChange={handleMarketingConsentChange}
                        checked={isMarketingConsentChecked}
                      />
                      <span className={styles.checkbox}></span>
                    </label>
                    <div className={styles.text}>
                      {data.marketingAndProfiling.description}
                    </div>
                  </div>
                </>
              )}
              {data.thirdPartyMarketing && (
                <div className={styles.consentContainer}>
                  <div className={styles.consentOption}>
                    <div className={styles.checkBoxTitle}>
                      {data.thirdPartyMarketing.title}
                    </div>
                    <label className={styles.checkBox}>
                      <input
                        type="checkbox"
                        style={{
                          cursor: "pointer",
                        }}
                        onChange={handleThirdPartyMarketingChange}
                        checked={isThirdPartyMarketingChecked}
                      />
                      {data.thirdPartyMarketing.description}
                    </label>
                  </div>
                </div>
              )}
              {data.thirdPartyProfiling && (
                <div className={styles.consentContainer}>
                  <div className={styles.consentOption}>
                    <div className={styles.checkBoxTitle}>
                      {data.thirdPartyProfiling.title}
                    </div>
                    <label className={styles.checkBox}>
                      <input
                        type="checkbox"
                        style={{
                          cursor: "pointer",
                        }}
                        onChange={handleThirdPartyProfilingChange}
                        checked={isThirdPartyProfilingChecked}
                      />
                      {data.thirdPartyProfiling.description}
                    </label>
                  </div>
                </div>
              )}
            </div>

            <button className={styles.expandButton} onClick={toggleExpand}>
              {!isExpanded
                ? data?.showButtonText || "Show Details"
                : !isFormValid() && (data?.hideButtonText || "Hide Details")}
            </button>
          </>
        )}
        <div className={styles.buttons}>
          {!dataUpdated && data.navigation && isFormValid() && (
            <button
              className={styles.redirectButton}
              disabled={!isFormValid()}
              onClick={handleFormSubmit}>
              {data?.submitButtonText || "Submit"}
            </button>
          )}
          {!data.hideButton && (
            <button className={styles.redirectButton} onClick={handleRedirect}>
              {data?.navigation?.nextButtonLabel}
            </button>
          )}
          {data?.contact_number && (
            <button
              className={`${styles.callButton} ${styles.redirectButton}`}
              onClick={redirectPhone}>
              {data?.contact_number?.nextButtonLabel || "Contact us now"}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default SuccessSlide;
